import { ChevronDown } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { styled } from "@pigment-css/react";
import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import { useState } from "react";
import { DateRangeSelectorDropdown } from "./DateRangeSelectorDropdown";
import { DateRangeCalendarModal } from "./DateRangeCalendarModal";
import { PeriodOption, type DateRange, type DateRangeSelectorProps } from "./types";

export const TriggerButton = styled("button")(({ theme }) => ({
  height: "50px",
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[8],
  padding: `0 ${theme.managementView.spacing[24]}`,
  backgroundColor: "transparent",
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
  borderRadius: "50px",
  cursor: "pointer",
  transition: "background-color 0.2s",
  fontFamily: theme.managementView.font.family,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[440],
  color: theme.managementView.colors.neutral[100],

  "& svg": {
    height: "12px",
    width: "12px",
  },

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  '&[data-state="open"]': {
    backgroundColor: theme.managementView.colors.neutral[4],
  },
}));

function formatDateRange(dateRange: DateRange, locale: string): string {
  const startDate = new Date(dateRange.startDate);
  const endDate = new Date(dateRange.endDate);

  const sameYear = startDate.getFullYear() === endDate.getFullYear();

  const baseOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
  };

  const startFormatted = startDate.toLocaleDateString(locale, {
    ...baseOptions,
    year: sameYear ? undefined : "numeric",
  });

  const endFormatted = endDate.toLocaleDateString(locale, {
    ...baseOptions,
    year: "numeric",
  });

  return `${startFormatted} - ${endFormatted}`;
}

export function usePeriodLabel(period: PeriodOption, customDateRange?: DateRange): string {
  const t = useTranslations("dateRange");
  const { locale } = useLocale();

  if (period === PeriodOption.CUSTOM && customDateRange) {
    return formatDateRange(customDateRange, locale);
  }

  const labels: Record<PeriodOption, string> = {
    THIS_WEEK: t("thisWeek"),
    THIS_MONTH: t("thisMonth"),
    LAST_MONTH: t("lastMonth"),
    LAST_3_MONTHS: t("last3Months"),
    LAST_6_MONTHS: t("last6Months"),
    LAST_12_MONTHS: t("last12Months"),
    CUSTOM: t("selectRange"),
  };

  return labels[period];
}

export const DateRangeSelector = ({
  selectedPeriod,
  customDateRange,
  onPeriodChange,
  onCustomDateRangeChange,
  excludedPeriods,
}: DateRangeSelectorProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const periodLabel = usePeriodLabel(selectedPeriod, customDateRange);

  const handleSelectRange = () => {
    setIsDropdownOpen(false);
    setIsCalendarOpen(true);
  };

  const handlePeriodSelect = (period: PeriodOption) => {
    onPeriodChange(period);
    setIsDropdownOpen(false);
  };

  const handleCalendarApply = (dateRange: DateRange) => {
    onPeriodChange(PeriodOption.CUSTOM);
    onCustomDateRangeChange?.(dateRange);
    setIsCalendarOpen(false);
  };

  const handleCalendarCancel = () => {
    setIsCalendarOpen(false);
  };

  return (
    <>
      <RadixDropdown.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen} modal={false}>
        <RadixDropdown.Trigger asChild>
          <TriggerButton type="button">
            {periodLabel}
            <ChevronDown />
          </TriggerButton>
        </RadixDropdown.Trigger>
        <RadixDropdown.Portal>
          <DateRangeSelectorDropdown
            selectedPeriod={selectedPeriod}
            onPeriodSelect={handlePeriodSelect}
            onSelectRange={handleSelectRange}
            excludedPeriods={excludedPeriods}
          />
        </RadixDropdown.Portal>
      </RadixDropdown.Root>
      {isCalendarOpen && (
        <DateRangeCalendarModal
          initialDateRange={customDateRange}
          onApply={handleCalendarApply}
          onCancel={handleCalendarCancel}
        />
      )}
    </>
  );
};
